import { useId } from "react"
import { motion, MotionConfig, AnimatePresence } from "framer-motion"

const variants = {
  pulse: {
    hidden: {
      r: 0,
      fillOpacity: 0.0,
      transition: {
        delay: 0,
        duration: 0.5,
        ease: "easeIn",
      },
    },
    visible: ({ opacity, from, n = 3 }) => ({
      r: from * 1.5,
      fillOpacity: opacity / n,
      transition: {
        delay: 0,
        duration: 1.0,
        ease: "easeOut",
      },
    }),
    pulsing: ({ i, opacity, from, to, delay }) => ({
      r: [from, to],
      fillOpacity: [0.0, opacity, 0.0],
      transition: {
        delay: delay + i * 0.6,
        duration: 2.0,
        ease: "easeOut",
        repeat: Infinity,
        times: [0, 0.0, 1],
        repeatDelay: 2.0,
      },
    }),
  },
  marker: {
    transition: {
      duration: 1.0,
      ease: "easeOut",
    },
  },
}

const Shadow = ({ id, x = 0, y = 0, blur = 0, color = "#000", opacity = 1.0 }) => (
  <filter id={id} patternUnits="userSpaceOnUse" x="-50%" y="-50%" width="200%" height="200%">
    <feDropShadow dx={x} dy={y} stdDeviation={blur} floodColor={color} floodOpacity={opacity} />
  </filter>
)

const Pulse = ({
  id = { id },
  n = 3,
  fill = "#fff",
  opacity = 0.6,
  from = 0,
  to = 1,
  isPulsing,
  delay,
}) => {
  const { hidden, visible, pulsing } = variants.pulse
  return (
    <g stroke="none" pointerEvents="none">
      {[...Array(n)].map((e, index) => (
        <motion.circle
          key={`${id}-pulse-${index}`}
          custom={{ i: index, opacity, from, to, delay }}
          fill={fill}
          fillOpacity={0.3}
          initial={hidden}
          animate={isPulsing ? pulsing : visible}
          exit={hidden}
        />
      ))}
    </g>
  )
}

const Marker = ({
  isVisible = true,
  isActive = false,
  onClick,
  className,
  boxSize = 144,
  markerSize = 56,
  strokeWidth = 6,
  stroke = "#fff",
  fill = "#FDCA00",
  isPulsing = false,
  pulseDelay = 0,
  ...styles
}) => {
  const id = useId()

  const viewBox = [-boxSize * 0.5, -boxSize * 0.5, boxSize, boxSize].join(" ")
  const radius = markerSize * 0.5

  return (
    <AnimatePresence key={`marker-${id}`}>
      {isVisible && (
        <motion.svg
          onClick={onClick}
          className={className}
          width={boxSize}
          height={boxSize}
          viewBox={viewBox}
          xmlns="http://www.w3.org/2000/svg"
          {...styles}
        >
          <defs>
            <Shadow id={`marker-shadow-${id}`} blur={(markerSize * 1) / 4} opacity={0.3} />
          </defs>
          <motion.g
            initial={{
              scale: 1.0,
            }}
            whileHover={
              isPulsing
                ? {
                    scale: 1.2,
                    transition: { duration: 1 },
                  }
                : null
            }
            whileTap={
              isPulsing
                ? {
                    scale: 0.9,
                  }
                : null
            }
            pointerEvents="all"
          >
            <Pulse
              id={id}
              from={radius}
              to={radius + 7 * strokeWidth}
              isPulsing={isPulsing}
              delay={pulseDelay}
              fill={isActive ? fill : stroke}
            />
            <MotionConfig {...variants.marker}>
              <motion.circle
                initial={{ r: 0 }}
                exit={{ r: 0 }}
                animate={{ r: radius + strokeWidth }}
                fill={fill}
                fillOpacity={0.6}
                stroke={"none"}
                filter={`url(#marker-shadow-${id})`}
              />
              <motion.circle
                initial={{ r: 0, strokeWidth: 0.0 }}
                exit={{ r: 0, strokeWidth: 0.0 }}
                animate={{ r: radius - strokeWidth * 0.5, strokeWidth }}
                fill={fill}
                stroke={stroke}
                strokeWidth={strokeWidth}
              />
            </MotionConfig>
          </motion.g>
        </motion.svg>
      )}
    </AnimatePresence>
  )
}

export default Marker
